import React, { useState } from "react";
import { EditScans } from "./modals/EditScans";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { isoDateToMDY } from "../../../utils/common";
import { ScanEye, ExternalLink } from "lucide-react";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabTable } from "../shared/components/TabTable";
import { TabToolbar } from "../shared/components/TabToolbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScanPreview } from "./modals/PreviewScan";

const GET_PARTICIPANT_BY_ODR_NUMBER = gql`
  query GetParticipantByODRNumber($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      scans {
        id
        filename
        filetype
        filelabel
        filedate
      }
    }
  }
`;

export const Scans = ({
  odrnumber,
  scans,
  loading,
  error,
}: {
  odrnumber: number;
  scans: any[];
  loading: boolean;
  error: any;
}) => {
  const [editScans, setEditScans] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);

  const TYPE_OPTIONS = [
    { value: "all", label: "All" },
    { value: "courtfile", label: "Court Document" },
    { value: "otherfile", label: "Other" },
    { value: "rapsheet", label: "Rap Sheet" },
  ];
  const TYPE_LABELS: Record<string, string> = {
    courtfile: "Court Document",
    otherfile: "Other",
    rapsheet: "Rap Sheet",
  };
  const TYPE_COLORS: Record<string, string> = {
    courtfile: "bg-blue-50 text-blue-700 border-blue-100",
    otherfile: "bg-gray-50 text-gray-700 border-gray-200",
    rapsheet: "bg-amber-50 text-amber-700 border-amber-100",
  };

  const normalizeType = (type: string | null | undefined) =>
    (type || "").toLowerCase().replace(/[\s_-]/g, "");

  if (loading) return <TabSpinner label="Loading scans..." />;
  if (error) return <div className="text-red-500">Error loading scans :(</div>;

  const filteredScans = (scans || []).filter((scan) => {
    if (typeFilter === "all") return true;
    const normalized = normalizeType(scan.filetype);
    return normalized === typeFilter;
  });

  return (
    <div className="space-y-4">
      <TabHeader
        icon={ScanEye}
        title="Scans"
        subtitle="Preview or manage participant scans"
        action={
          <Button
            variant="outline"
            size="sm"
            className="bg-blue-600 text-white hover:bg-blue-700 border-0 h-8 px-3 text-sm"
            onClick={() => setEditScans(true)}
          >
            Edit Scans
          </Button>
        }
      />
      <TabToolbar
        left={
          <div className="flex items-center gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={typeFilter === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setTypeFilter(opt.value)}
                className="h-8"
              >
                {opt.label}
              </Button>
            ))}
          </div>
        }
      />

      <Card className="rounded-none border-0 shadow-none">
        <CardContent className="p-0">
          <div className="border-t border-b border-gray-200 overflow-hidden">
            <TabTable<any>
              data={filteredScans}
              emptyMessage="No scans available."
              columns={[
                { title: "Date", className: "w-[140px]", render: (s) => isoDateToMDY(s.filedate) || "—" },
                { title: "Label", render: (s) => s.filelabel || s.filename || "Untitled" },
                { title: "Type", className: "w-[160px]", render: (s) => {
                  const normalized = normalizeType(s.filetype);
                  const label = TYPE_LABELS[normalized] || s.filetype || "—";
                  const color = TYPE_COLORS[normalized] || "bg-gray-50 text-gray-700 border-gray-200";
                  return (
                    <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${color}`}>
                      {label}
                    </span>
                  );
                }},
                { title: "View", className: "w-[80px] text-right", render: (s) => (
                  <button
                    className="inline-flex items-center gap-1 text-blue-600 text-sm hover:underline"
                    onClick={() => setSelectedScanId(s.id)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View
                  </button>
                )},
              ]}
            />
          </div>
        </CardContent>
      </Card>

      <EditScans
        isOpen={editScans}
        onClose={() => setEditScans(false)}
        scans={scans}
        odrNumber={odrnumber}
        programNumber={1}
      />

      {selectedScanId && (
        <ScanPreview
          odrnumber={odrnumber}
          scan={selectedScanId}
          close={() => setSelectedScanId(null)}
        />
      )}
    </div>
  );
};

export default Scans;
